'use client'

import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useCallback } from 'react'

import { fetchMasterInstrumentList } from '@/service/master-instrument.api'
import { Exchange, InstrumentList } from '@/types/generated/common/types'

export const masterInstrumentKeys = {
  all: ['master-instrument'] as const,
  list: (exchange: Exchange) => [...masterInstrumentKeys.all, 'list', exchange] as const,
}

export const useMasterInstrumentList = (exchange: Exchange, enabled: boolean) => {
  return useQuery({
    queryKey: masterInstrumentKeys.list(exchange),
    queryFn: () => fetchMasterInstrumentList({ exchange }),
    // The catalog is regenerated on the backend at most every ~10 minutes
    // (curation reconcile) — no reason to refetch more often than that.
    staleTime: 10 * 60 * 1000,
    // Same reasoning as instrumentListQueryOptions: a large, slow-changing
    // catalog that a focus/reconnect storm must not re-download in bulk.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled,
    // A venue with no catalog yet is a deterministic "not available yet"
    // response, not a flaky network error — don't retry it 3x with backoff.
    retry: false,
  })
}

export const useMasterInstrumentLists = (exchanges: Exchange[], enabled: boolean) => {
  const combine = useCallback(
    (results: UseQueryResult<InstrumentList>[]) => {
      // `results.every(...)` is vacuously true on an empty array, so an
      // `exchanges` list filtered down to zero entries (e.g. every venue
      // currently disabled) must be handled explicitly — otherwise it reads
      // as either "still loading" or "everything errored" with nothing
      // actually queried.
      if (results.length === 0) {
        return { instruments: [], isLoading: false, isError: false }
      }
      const isSettled = results.every((result) => result.isFetched)
      // Return whatever's already fetched rather than waiting for every exchange to
      // settle — with more exchanges in the catalog (see SUPPORTED_EXCHANGES), one
      // slow venue (e.g. a large options catalog) would otherwise hold back results
      // from every other exchange that already came back. `isLoading` still reflects
      // whether everything has settled, so callers' loading indicator is unchanged.
      return {
        instruments: results.flatMap((result) => result.data?.instruments ?? []),
        isLoading: enabled && !isSettled,
        isError: isSettled && results.every((result) => result.isError),
      }
    },
    [enabled]
  )

  return useQueries({
    queries: exchanges.map((exchange) => ({
      queryKey: masterInstrumentKeys.list(exchange),
      queryFn: () => fetchMasterInstrumentList({ exchange }),
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      enabled,
      retry: false,
    })),
    combine,
  })
}
